import { Button, ConfigProvider, Input, Modal } from 'antd';
import { useEffect, useRef, useState } from 'react';
import colorTokens from '../../constants/design_system/colors';
import { ViewColumnComponentType, ViewColumnsGroup } from './components/ViewColumnsGroup';
import { InfoCircleFilled, ProjectOutlined, TableOutlined } from '@ant-design/icons';
import {
  WorkspaceView,
  WorkspaceViewEntityType,
  WorkspaceViewGroup,
  WorkspaceViewRepresentation,
} from '../../types/WorkspaceView';
import ViewRepresentationCard from './components/ViewRepresentationCard';
import { Column, ViewColumn, ViewColumnUtils } from '../../types/WorkspaceViewColumn';
import { CustomSecondaryButton } from '../UI/CustomSecondaryButton';
import { useGetWorkspace } from '../../serverState/workspace';
import { WorkspaceFramework } from '../../types/Workspace';
import { WorkspaceViewSort, WorkspaceViewSortDirection } from '../../types/WorkspaceViewSortBy';
import { CustomSelect } from '../UI/CustomSelect';
import { getWorkspaceViewGroup } from '../../common/viewUtils';
import { SortAscending } from '../../assets/CustomIcons/SortAscending';
import { SortDescending } from '../../assets/CustomIcons/SortDescending';

interface NewViewModalProps {
  open: boolean;
  onOk: (
    name: string,
    representation: WorkspaceViewRepresentation,
    entityType: WorkspaceViewEntityType,
    columns: ViewColumn[],
    groupBy: string,
    sortBy: WorkspaceViewSort
  ) => void;
  onCancel: () => void;
  views: WorkspaceView[];
}

export const NewSpaceViewModal = (props: NewViewModalProps) => {
  const { open, onCancel, onOk, views } = props;

  const workspaceQuery = useGetWorkspace();
  const framework = workspaceQuery.data?.framework || WorkspaceFramework.none;

  const [name, setName] = useState<string>('View');
  const [representation, setRepresentation] = useState<WorkspaceViewRepresentation>(WorkspaceViewRepresentation.table);
  const [entityType, setEntityType] = useState<WorkspaceViewEntityType>(WorkspaceViewEntityType.task);

  const [groupBy, setGroupBy] = useState<string>(Column.epic);
  const [sortBy, setSortBy] = useState<string>('Manual');
  const [sortDirection, setSortDirection] = useState<WorkspaceViewSortDirection>(WorkspaceViewSortDirection.desc);

  const group: WorkspaceViewGroup = getWorkspaceViewGroup(location.pathname);
  const [columns, setColumns] = useState<ViewColumn[]>(ViewColumnUtils.defaultColumns(framework, { group }));

  const viewNameInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(generateDefaultName());
      setRepresentation(WorkspaceViewRepresentation.table);
      setEntityType(group === WorkspaceViewGroup.epics ? WorkspaceViewEntityType.epic : WorkspaceViewEntityType.task);
      setGroupBy(Column.epic);
      setSortBy('Manual');
      setSortDirection(WorkspaceViewSortDirection.desc);
      setColumns(ViewColumnUtils.defaultColumns(framework, { group }));
    }
  }, [open]);

  useEffect(() => {
    setColumns(ViewColumnUtils.defaultColumns(framework, { group, entityType }));
  }, [representation, entityType]);

  const updatePropertySelection = (propertyId: string, isSelected: boolean) => {
    setColumns((prevProperties) =>
      prevProperties.map((property) => (property.id === propertyId ? { ...property, selected: isSelected } : property))
    );
  };

  const updateName = (e) => {
    const newValue: string = e.target.value;
    setName(newValue);
  };

  const generateDefaultName = () => {
    const baseName = 'View';
    let newName = baseName;
    let counter = 1;

    const titles = views.map((view) => view.name);

    while (titles.includes(newName)) {
      counter += 1;
      newName = `${baseName} ${counter}`;
    }

    return newName;
  };

  return (
    <Modal
      open={open}
      title={<div className="text-brand-grey-dark text-base font-semibold leading-normal ml-5">Create a new view</div>}
      okText={'Create View'}
      cancelText={'Cancel'}
      onCancel={onCancel}
      onOk={() => {
        const trimmedName = name.trim();
        if (trimmedName) {
          onOk(trimmedName, representation, entityType, columns, groupBy, { column: sortBy, direction: sortDirection });
        }
      }}
      footer={
        <div className="flex justify-between items-center mt-5">
          <CustomSecondaryButton type="delete" onClick={onCancel}>
            <div className="text-red-600 text-base font-semibold">Cancel</div>
          </CustomSecondaryButton>
          <Button
            type="primary"
            onClick={() => {
              const trimmedName = name.trim();
              if (trimmedName) {
                onOk(trimmedName, representation, entityType, columns, groupBy, {
                  column: sortBy,
                  direction: sortDirection,
                });
              }
            }}
          >
            <div className="text-brand-white text-base font-semibold">Create View</div>
          </Button>
        </div>
      }
      afterOpenChange={(open) => {
        if (open) {
          viewNameInputRef?.current?.focus();
          viewNameInputRef?.current?.select();
        }
      }}
      width={640}
      centered
    >
      <div className="flex mt-5">
        <div className="flex flex-col flex-1 gap-2 overflow-y-auto px-5 mt-2">
          <div className="text-neutral-900 text-xs font-semibold leading-none">View Name</div>
          <div className="flex items-center gap-2 mt-2">
            {representation === WorkspaceViewRepresentation.table && <TableOutlined className="text-neutral-900" />}
            {representation === WorkspaceViewRepresentation.kanban && <ProjectOutlined className="text-neutral-900" />}
            <Input
              className="h-7 text-brand-grey-dark text-sm font-normal box-border"
              size={'small'}
              ref={viewNameInputRef}
              value={name}
              onChange={updateName}
              onPressEnter={updateName}
              onBlur={updateName}
            />
          </div>

          <div className="text-neutral-900 text-xs font-semibold mt-2">Layout</div>

          <div className="h-16 p-1 bg-neutral-300 rounded justify-center items-start inline-flex">
            <ViewRepresentationCard
              representation={WorkspaceViewRepresentation.table}
              setRepresentation={() => setRepresentation(WorkspaceViewRepresentation.table)}
              selected={representation === WorkspaceViewRepresentation.table}
              viewGroup={group}
              entityType={entityType}
            />

            <ViewRepresentationCard
              representation={WorkspaceViewRepresentation.kanban}
              setRepresentation={() => setRepresentation(WorkspaceViewRepresentation.kanban)}
              selected={representation === WorkspaceViewRepresentation.kanban}
              viewGroup={group}
              entityType={entityType}
            />
          </div>

          {entityType !== WorkspaceViewEntityType.epic && (
            <div className="flex items-center justify-between mt-2">
              <div className="text-neutral-900 text-xs font-semibold">Group by:</div>
              <CustomSelect
                options={ViewColumnUtils.groupByColumns(framework, { group, entityType }).map((c) => c.id)}
                selectedOption={groupBy}
                selectOption={(selectedGroupBy) => setGroupBy(selectedGroupBy)}
                getTitle={(option) => ViewColumnUtils.getTitle(option as Column, framework)}
                width="150px"
              />
            </div>
          )}

          <div className="flex items-center justify-between mt-2">
            <div className="text-neutral-900 text-xs font-semibold">Sort by:</div>
            <div className="w-[150px] flex items-center">
              <CustomSelect
                options={['Manual', ...ViewColumnUtils.sortByColumns(framework, { group }).map((c) => c.id)]}
                selectedOption={sortBy}
                selectOption={(selectedSortBy) => setSortBy(selectedSortBy)}
                getTitle={(option) =>
                  option === 'Manual' ? 'Manual' : ViewColumnUtils.getTitle(option as Column, framework)
                }
                className="flex-1"
              />
              {sortBy !== 'Manual' && (
                <div
                  className={`h-[23px] w-[23px] flex items-center justify-center rounded border border-solid border-neutral-500 text-neutral-900 hover:border-brand-taskonio-green cursor-pointer ml-1`}
                  onClick={() => {
                    setSortDirection(
                      sortDirection === WorkspaceViewSortDirection.asc
                        ? WorkspaceViewSortDirection.desc
                        : WorkspaceViewSortDirection.asc
                    );
                  }}
                >
                  {sortDirection === WorkspaceViewSortDirection.asc ? <SortAscending /> : <SortDescending />}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="w-px min-h-full bg-neutral-300 rounded-sm" />
        <div className="mx-4 flex flex-col flex-[1.2]">
          <>
            <div className="text-neutral-900 text-xs font-semibold leading-none my-2">Properties</div>
            <ConfigProvider
              theme={{
                token: {
                  colorPrimary: colorTokens.blue[600],
                },
              }}
            >
              <ViewColumnsGroup
                componentType={ViewColumnComponentType.newSpaceViewModal}
                columns={columns}
                updateColumns={(newProperties) => setColumns(newProperties)}
                updateColumnSelection={updatePropertySelection}
                representation={representation}
              />
            </ConfigProvider>
            <div className="pl-2 py-2 bg-neutral-200 rounded justify-start items-center inline-flex gap-2 mt-2">
              <InfoCircleFilled className="text-neutral-800" />
              <div className="text-neutral-900 text-xs font-normal leading-none">
                Values of your hidden properties are saved.
              </div>
            </div>
          </>
        </div>
      </div>
    </Modal>
  );
};
